<template>

  <main class="content">
    <Hero :title="data?.title" small />
    <div class="maxw--waist block">
      <div class="image_header" v-if="thumbnail.id">
        <StrapiImageHazy 
          loading="lazy" 
          :placeholder="thumbnail.placeholder"
          :src="getFlattenedStrapiImageLocation(thumbnail)" 
          provider="strapi"
        />
      </div>
      <span class="subinfo mb-2">
        <Icon name="ic:baseline-event-note" class="icon--md" /> Gepubliceerd op: {{ publishedAt }}
      </span>
      <CategoryHolder :items="categories" v-if="hasCategories" class="mb-2"/>
      <article class="rendered" v-html="$md.render(data?.content || '')"></article>
      <div class="blog__attachments">
        <Attachment 
          v-for="attachment in attachments"
          :key="attachment.id"
          :name="attachment.name"
          :src="getFlattenedStrapiImageLocation(attachment)"
        />
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import type BlogArticle from "@/typings/strapi/objects/blogArticle";
import type Tag from "~/typings/items/tag";

const route = useRoute();
const { findOne } = useStrapi();

const { data } = await useAsyncData(
  'blog-page',
  () => findOne<BlogArticle>('slugify/slugs/blog-article/' + route.params.slug,
    {
      populate: ['thumbnail', 'attachments', 'categories']
    }
  ),
  {
    transform: flattenObject
  }
)

const dayjs = useDayjs();
const publishedAt = computed(() => dayjs(data.value?.publishedAt).format('D MMMM YYYY'));
const attachments = computed(() => flattenCollection(data.value?.attachments));
const thumbnail = computed(() => flattenObject(data.value?.thumbnail));
const categories = computed(() => {
  const catgrs = flattenCollection(data.value?.categories).map<Tag>(c => ({
    id: c.id,
    title: c.title,
    type: 'default'
  }));

  if (data.value?.private) {
    catgrs.unshift({
      title: 'Privé',
      type: 'exclusive',
      id: 0
    });
  }

  return catgrs;
});
const hasCategories = computed(() => categories.value && categories.value.length > 0);

useHead({
  title: data.value?.title
})

definePageMeta({
  pageTransition: pageTransitionConfig,
});
</script>

<style lang="scss">
.image_header {
  max-width: 100%;
  margin-bottom: 3rem;
  padding-bottom: 0.5rem;
  padding-right: 0.5rem;
  position: relative;

  img {
    width: 100%;
    object-fit: cover;
    z-index: 2;
  }

  &::after {
    content: '';
    width: 100%;
    height: 100%;
    border: 2px solid $text;
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    z-index: -1;
  }
}

.blog__attachments {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 1rem;
  margin-top: 4rem;
}
</style>
